import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {serviceEventTicketPoliciesSelector, useGetAllServiceEventTicketPoliciesQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {useNavigate} from "react-router-dom";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {
  ServiceEventTicketPolicyDataTable
} from "../../components/data-table/ServiceEventTicketPolicyDataTable/ServiceEventTicketPolicyDataTable";

export type ServiceEventTicketPolicyPageProps = {}

export const ServiceEventTicketPolicyDataTablePageRoute = () => {
  return (
      <ServiceEventTicketPolicyDataTablePage/>
  );
};

export const ServiceEventTicketPolicyDataTablePage = (props: ServiceEventTicketPolicyPageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectServiceEventTicketPolicies = useMemo(() => {
    return serviceEventTicketPoliciesSelector();
  }, []);

  const {
    policies,
    isLoading,
    error,
    refetch,
  } = useGetAllServiceEventTicketPoliciesQuery(access ? (regionId ? { regionId: regionId } : skipToken) : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      policies: selectServiceEventTicketPolicies(result, {regionId: regionId}),
      isLoading: result.isLoading,
      error: result.error,
    })
  });

  return (
      <ProtectedPage
          permissions={["serviceeventticketpolicy_view"]}
          isLoading={isLoading}
          error={error}
          onAccess={setAccess}>
        {(classes) => (
            <ResizeableContent>
              {(size) => (
                  <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
                    <Stack h="100%" m="sm">
                      <RouteBreadcrumbs/>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <Group position="apart">
                          <Title order={4}>
                            {t("common.entity.serviceeventticketpolicy.plural", "Service Event Ticket Policies", {ns: "common"})}
                          </Title>
                          <Menu
                              width={260}
                              position="bottom-end"
                              transitionProps={{transition: "pop-top-right"}}
                              withinPortal
                          >
                            <Menu.Target>
                              <UnstyledButton>
                                <Group spacing={7}>
                                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                                    {t("common.actions.plural", "Actions", {ns: "common"})}
                                  </Text>
                                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                                </Group>
                              </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => refetch()}>
                                {t("common.actions.refresh", "Refresh", {ns: "common"})}
                              </Menu.Item>
                              <HasAccess permissions={["serviceeventticketpolicy_create"]}>
                                <>
                                  <Menu.Divider/>
                                  <Menu.Item onClick={() => navigate("/service-event-ticket-policies/form")}>
                                    {t("common.action.new", "New", {ns: "common"})}
                                  </Menu.Item>
                                </>
                              </HasAccess>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Card>
                      <ServiceEventTicketPolicyDataTable
                          withBorder
                          records={policies}
                          recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                          fetching={isLoading}/>
                    </Stack>
                  </Container>
              )}
            </ResizeableContent>
        )}
      </ProtectedPage>
  );
};
